import { UserProfileRole } from '@@types/api.types';
import { MainLayout } from '@components/layouts/main.layout';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { useIntelOrganizationsQuery } from '@queries/intel-organizations.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import type { FC } from 'react';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { IntelOrganizationsFilterBar } from './components/intel-organizations-filter-bar.component';
import { IntelOrganizationsPagination } from './components/intel-organizations-pagination.component';
import { IntelOrganizationsTable } from './components/intel-organizations-table.component';
import { useIntelOrganizationsCatalogQueryParams } from './hooks/use-intel-organizations-catalog-query-params.hook';

export const IntelOrganizationsPage: FC = () => {
  const { data: user, isPending: isUserPending } = useGetCurrentUser();
  const isIntel = user?.profile?.role === UserProfileRole.INTELLIGENCE;

  const {
    DEFAULT_SORTING,
    handleLimitChange,
    page,
    queryParams,
    search,
    setPage,
    setSearch,
    setSorting,
    setType,
    sortingState,
    type,
  } = useIntelOrganizationsCatalogQueryParams();

  const {
    data: organizationsData,
    isFetching,
    isPending: isOrganizationsPending,
  } = useIntelOrganizationsQuery({
    ...queryParams,
    enabled: isIntel,
  });

  const organizations = organizationsData?.companies ?? [];
  const pagination = organizationsData?.pagination;
  const hasCatalogFilters = Boolean(search?.trim()) || type !== null;
  const showAccessEmptyState = !isOrganizationsPending && !hasCatalogFilters && pagination?.totalItems === 0;

  useEffect(() => {
    if (!pagination) return;

    const total = pagination.totalPages ?? 0;

    if (total < 1) {
      if (page !== 1) void setPage(1);
      return;
    }

    if (page > total) {
      void setPage(Math.max(1, total));
    }
  }, [page, pagination, setPage]);

  if (isUserPending) {
    return null;
  }

  if (!isIntel) {
    return <Navigate to={routes.home.path} />;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title="Intel Organizations" />
      <MainLayout fullWidth>
        <div className="mx-auto flex w-full max-w-(--breakpoint-lg) flex-col gap-4">
          <Tile header={{ title: 'Organizations' }}>
            <div className="flex flex-col gap-4">
              {!showAccessEmptyState && (
                <IntelOrganizationsFilterBar
                  onPageReset={() => void setPage(1)}
                  search={search}
                  setSearch={setSearch}
                  setType={setType}
                  type={type}
                />
              )}

              {showAccessEmptyState ? (
                <EmptyStateContainer
                  className="min-h-72 rounded-xl border"
                  message="Ask an administrator to confirm your access"
                  title="No Organizations Available"
                />
              ) : (
                <>
                  <IntelOrganizationsTable
                    isLoading={isOrganizationsPending}
                    onSortingChange={(state) => {
                      if (!state || state.length === 0) {
                        void setSorting(DEFAULT_SORTING.map((item) => ({ ...item })));
                        return;
                      }
                      void setPage(1);
                      void setSorting(state);
                    }}
                    organizations={organizations}
                    sorting={sortingState}
                  />

                  {pagination && (
                    <IntelOrganizationsPagination
                      isFetching={isFetching}
                      onPageChange={(nextPage) => void setPage(Math.max(1, nextPage))}
                      onPageSizeChange={handleLimitChange}
                      pagination={pagination}
                    />
                  )}
                </>
              )}
            </div>
          </Tile>
        </div>
      </MainLayout>
    </PageviewTracker>
  );
};
